import { useMsal } from "@azure/msal-react";
import BusinessIcon from "@mui/icons-material/Business";
import CloudIcon from "@mui/icons-material/Cloud";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Avatar, ListItemAvatar, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from "@mui/material";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EnvironmentViewModel, TenantViewModel, UserClient, UserViewModel } from "../../api/api";
import { useApiClient } from "../../common/hooks";
import BundleContext from "../../UserInterfaceModule/Contexts/BundleContext";
import AuthContext from "../AuthContext";
import EnvironmentSelectorDialog from "./EnvironmentSelectorDialog";
import TenantSelectorDialog from "./TenantSelectorDialog";

interface MenuListItemAvatarProps {
  open: boolean;
  additionalSx?: any;
}

export default function MenuListItemAvatar({ open, additionalSx }: MenuListItemAvatarProps) {
  const auth = useContext(AuthContext);
  const { accounts, inProgress } = useMsal();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [environmentDialogOpen, setEnvironmentDialogOpen] = useState(false);
  const [userData, setUserData] = useState<UserViewModel | null>(null);
  const loadedAccountUsername = useRef<string | null>(null);
  const userClient = useApiClient(UserClient);
  const bundleContext = useContext(BundleContext);

  const account = useMemo(() => (accounts.length > 0 ? accounts[0] : undefined), [accounts]);

  // Load user data including current tenant and environment when account is available
  const loadUserData = useCallback(async () => {
    // Wait for MSAL to finish initialization and ensure we have an account
    if (!account || inProgress !== "none") {
      return;
    }

    // Do not load user data if not authenticated
    if (!auth.isAuthenticated) {
      return;
    }

    // Prevent loading if already loaded for this account
    if (loadedAccountUsername.current === account.username) {
      console.log("User data already loaded for account:", account.username);
      return;
    }

    // Set the flag immediately to prevent duplicate calls (especially in StrictMode).
    // We'll clear it below if we decide not to proceed.
    loadedAccountUsername.current = account.username;

    // Verify we can actually obtain a token before hitting the endpoint.
    // If the cached session is expired (no valid refresh token), getAccessToken()
    // returns undefined and we skip the call entirely — avoiding a 401 that would
    // otherwise trigger an unwanted sign-in redirect while on a public page.
    const token = await auth.authService.getAccessToken();
    if (!token) {
      console.warn("No access token available, skipping user data load.");
      loadedAccountUsername.current = null;
      return;
    }

    try {
      console.log("Loading user data for account:", account.username);
      const user = await userClient.getUser();
      setUserData(user);
    } catch (error) {
      console.error("Error loading user data:", error);
      // Reset the flag on error so it can retry later
      loadedAccountUsername.current = null;
    }
  }, [account, inProgress, userClient, auth.isAuthenticated, auth.authService]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignIn = async () => {
    handleClose();
    try {
      await auth.authService.signIn();
    } catch (error) {
      console.error("Sign-in error:", error);
    }
  };

  const handleSignOut = () => {
    handleClose();
    auth.authService.signOut();
  };

  const handleChangeTenant = () => {
    handleClose();
    setTenantDialogOpen(true);
  };

  const handleChangeEnvironment = () => {
    handleClose();
    setEnvironmentDialogOpen(true);
  };

  const handleTenantChanged = async (tenant: TenantViewModel) => {
    // Update the user data with the new tenant
    if (userData) {
      setUserData({
        ...userData,
        tenant: tenant,
      });
    }

    // Reset loaded account username so user data will be reloaded
    loadedAccountUsername.current = null;

    // Reload user data to get fresh tenant information
    await loadUserData();

    // Refresh the bundle context to load data for the new tenant
    await bundleContext.Refresh();

    // Navigate to root to avoid using cached data from the previous tenant
    navigate("/");
  };

  const handleEnvironmentChanged = async (environment: EnvironmentViewModel) => {
    // Update the user data with the new environment
    if (userData) {
      setUserData({
        ...userData,
        environment: environment,
      });
    }

    // Reset loaded account username so user data will be reloaded
    loadedAccountUsername.current = null;

    // Reload user data to get fresh environment information
    await loadUserData();

    // Navigate to root to avoid using cached data from the previous environment
    navigate("/");
  };

  const userName = auth.account?.name || auth.account?.displayName || "Guest";
  const userEmail = auth.account?.username || auth.account?.email || "";
  const initials = userName
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        sx={[
          {
            minHeight: 48,
            px: 2.5,
          },
          open
            ? {
                justifyContent: "initial",
              }
            : {
                justifyContent: "center",
              },
          additionalSx,
        ]}
      >
        <ListItemAvatar
          sx={[
            {
              minWidth: 0,
            },
            open
              ? {
                  mr: 2,
                }
              : {
                  mr: "auto",
                },
          ]}
        >
          <Avatar sx={{ width: 32, height: 32 }}>{initials}</Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography variant="body2" noWrap>
              {userName}
            </Typography>
          }
          secondary={
            <Typography variant="caption" noWrap>
              {userEmail}
            </Typography>
          }
          sx={[
            open
              ? {
                  opacity: 1,
                }
              : {
                  opacity: 0,
                },
          ]}
        />
      </ListItemButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {!auth.isAuthenticated ? (
          <MenuItem onClick={handleSignIn}>
            <ListItemIcon>
              <LoginIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Sign In</ListItemText>
          </MenuItem>
        ) : (
          <>
            <MenuItem key="info" onClick={handleClose} disabled>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                Profile: {auth.account?.name} ({auth.account?.username})
              </ListItemText>
            </MenuItem>
            {userData?.tenant && (
              <MenuItem key="current-tenant" onClick={handleClose} disabled>
                <ListItemIcon>
                  <BusinessIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Tenant: {userData.tenant.code}</ListItemText>
              </MenuItem>
            )}
            {userData?.tenants && userData.tenants.length > 0 && (
              <MenuItem key="change-tenant" onClick={handleChangeTenant}>
                <ListItemIcon>
                  <SwapHorizIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Change Tenant</ListItemText>
              </MenuItem>
            )}
            {userData?.environment && (
              <MenuItem key="current-environment" onClick={handleClose} disabled>
                <ListItemIcon>
                  <CloudIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Environment: {userData.environment.code}</ListItemText>
              </MenuItem>
            )}
            {userData?.environments && userData.environments.length > 0 && (
              <MenuItem key="change-environment" onClick={handleChangeEnvironment}>
                <ListItemIcon>
                  <SwapHorizIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Change Environment</ListItemText>
              </MenuItem>
            )}
            <MenuItem key="sign-out" onClick={handleSignOut}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Sign Out</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
      <TenantSelectorDialog
        open={tenantDialogOpen}
        onClose={() => setTenantDialogOpen(false)}
        onTenantChanged={handleTenantChanged}
        availableTenants={userData?.tenants}
      />
      <EnvironmentSelectorDialog
        open={environmentDialogOpen}
        onClose={() => setEnvironmentDialogOpen(false)}
        onEnvironmentChanged={handleEnvironmentChanged}
        availableEnvironments={userData?.environments}
      />
    </>
  );
}
